import { useCallback, useContext, useEffect } from "react";

import { useMutation } from "@apollo/client";
import { navigate } from "gatsby";

import { SessionStatus } from "@/autoGeneratedGlobalTypes";
import GlobalContext from "@/contexts/Global/GlobalContext";
import { isQuerySuccess } from "@/utils/apolloUtils";

import { endSession as endSessionType, endSessionVariables } from "./graphql/__generated__/endSession";
import { END_SESSION } from "./graphql/END_SESSION";
import type { UseEndSessionProps, UseEndSessionReturn } from "./types";

export type { UseEndSessionProps, UseEndSessionReturn } from "./types";

/**
 * Hook для завершения сессии
 *
 * Управляет процессом завершения сессии и навигацией на страницу отзыва
 */
export const useEndSession = ({
  sessionId,
  receiver,
  isExpert,
  sessionType,
  onSessionFailed,
}: UseEndSessionProps): UseEndSessionReturn => {
  const { setAlertStatus, setIsAlertShown } = useContext(GlobalContext);

  const [endSession, { loading, error, data }] = useMutation<endSessionType, endSessionVariables>(END_SESSION);
  const handleEndSession = useCallback(() => {
    if (!sessionId) {
      return;
    }

    endSession({ variables: { sessionID: sessionId } });
  }, [endSession, sessionId]);

  // Обработка результата завершения сессии
  useEffect(() => {
    if (data && isQuerySuccess(loading, error)) {
      const { durationInMinutes, totalPrice, status } = data.endSession;

      setAlertStatus(null);
      setIsAlertShown(false);

      // Недозвон (сессия не тарифицировалась): экран завершения с нулями не показываем —
      // отдаём решение вызывающему, он рисует модалку «Не удалось дозвониться».
      if (status === SessionStatus.failed && onSessionFailed) {
        onSessionFailed();
        return;
      }

      navigate("/chat/review", {
        state: {
          receiver,
          durationInMinutes,
          totalPrice,
          sessionID: sessionId,
          sessionType,
          isExpert,
        },
      });
    }
  }, [
    data,
    error,
    loading,
    isExpert,
    onSessionFailed,
    receiver,
    sessionId,
    setAlertStatus,
    setIsAlertShown,
    sessionType,
  ]);

  return {
    handleEndSession,
    loading,
  };
};
